// 手机子页面：记录表按字段展示，比较表保留横向关系；扩大原生按钮触控区域，电脑布局及业务事件不变。
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { Calculator, History, Loader2, PlugZap, Save, Sofa, Wrench, Hammer } from "lucide-react";
import { TradeFormulaTable } from "./TradeFormulaTable";
import { PricingPreview } from "./PricingPreview";
import { CatalogTable } from "./CatalogTable";
import { PricingVersions } from "./PricingVersions";
import { PanelTable } from "./PanelTable";
import { fmtDT } from "../../lib/date";

const TABS = [
  { k: "trades", zh: "工种公式", en: "Trade formulas", icon: Calculator },
  { k: "panel", zh: "配电箱费用表", en: "Panel costs", icon: PlugZap },
  { k: "reno", zh: "装修单项价目", en: "Renovation items", icon: Hammer },
  { k: "mat", zh: "室内材料目录", en: "Interior materials", icon: Sofa },
  { k: "versions", zh: "版本历史", en: "Versions", icon: History },
];

/* 管理员：工种定价公式 / 配电箱费用表 / 装修单项 / 材料目录 — 草稿编辑 + 实时预览 + 保存留版本 + 回滚 */
export const TradePricingEditor = ({ compact = false, onSaved }) => {
  const { api, lang, user } = useApp();
  const zh = lang === "zh";
  const canEdit = ["admin", "eng_manager", "ceo"].includes(user?.role);
  const [meta, setMeta] = useState(null);
  const [draft, setDraft] = useState(null);
  const [params, setParams] = useState(null);
  const [preview, setPreview] = useState([]);
  const [tab, setTab] = useState("trades");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const timer = useRef(null);

  const apply = useCallback((d) => {
    setMeta(d);
    setDraft({ trades: Object.fromEntries(d.trades.map((r) => [r.key, r.config])), panel: d.panel, renoItems: d.renoItems, matItems: d.matItems, varianceThreshold: d.varianceThreshold ?? 15 });
    setParams((p) => p || d.sampleParams);
    setPreview(d.trades);
    setDirty(false);
  }, []);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/trade-pricing"); apply(data); } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  }, [api, apply]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!draft || !params) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { const { data } = await api.post("/trade-pricing/preview", { trades: draft.trades, panel: draft.panel, params }); setPreview(data.rows); } catch { /* keep last */ }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [api, draft, params]);

  const patch = (k, v) => { setDraft((d) => ({ ...d, [k]: v })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/trade-pricing", { ...draft, note });
      apply(data); setNote("");
      toast.success(zh ? `已保存新版本 ${data.version}，之后自动生成的合同/RFQ 参考预算将按新公式计算` : `Saved ${data.version}`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setSaving(false);
  };
  const rows = useMemo(() => (meta?.trades || []).map((r) => ({ ...r, config: draft?.trades?.[r.key] || r.config })), [meta, draft]);
  if (!meta || !draft) return <p className="text-sm text-slate-400 flex items-center gap-2 p-6" data-testid="trade-pricing-loading"><Loader2 className="h-4 w-4 animate-spin" />{zh ? "加载定价公式…" : "Loading…"}</p>;

  return (
    <div className="flex flex-col gap-3" data-testid="trade-pricing-editor">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1" data-testid="trade-pricing-tabs">
          {TABS.map((t) => <button key={t.k} data-testid={`trade-pricing-tab-${t.k}`} onClick={() => setTab(t.k)} className={"max-md:min-h-11 max-md:min-w-11 " + (`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${tab === t.k ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`)}><t.icon className="h-3.5 w-3.5" />{zh ? t.zh : t.en}{t.k === "reno" && ` (${draft.renoItems.length})`}{t.k === "mat" && ` (${draft.matItems.length})`}</button>)}
        </div>
        <p className="ml-auto text-[11px] text-slate-400" data-testid="trade-pricing-version">{zh ? "当前版本" : "Version"} <b className="text-slate-600">{meta.version}</b>{meta.updatedAt && ` · ${fmtDT(meta.updatedAt)} · ${meta.updatedBy}`}</p>
      </div>
      {tab === "trades" && (
        <div className={`grid gap-3 ${compact ? "grid-cols-1" : "grid-cols-1 xl:grid-cols-[1fr_360px]"}`}>
          <div className="space-y-3">
            <TradeFormulaTable api={api} zh={zh} rows={rows} variables={meta.variables} modes={meta.modes} panel={draft.panel} canEdit={canEdit} onChange={(key, cfg) => patch("trades", { ...draft.trades, [key]: cfg })} />
            <div className="rounded-xl border border-red-100 bg-red-50/40 px-3 py-2 flex flex-wrap items-center gap-2 text-xs text-slate-700" data-testid="variance-threshold-card">
              <span className="font-semibold">{zh ? "报价超参考预算标红阈值" : "Over-budget flag threshold"}</span>
              <input type="number" min="0" max="200" step="1" data-testid="variance-threshold-input" disabled={!canEdit} value={draft.varianceThreshold ?? ""} onChange={(e) => patch("varianceThreshold", e.target.value === "" ? "" : Number(e.target.value))} className="h-7 w-20 rounded-md border border-slate-200 px-2 text-xs bg-white text-right disabled:bg-slate-50" />
              <span>%</span>
              <span className="text-[11px] text-slate-500">{zh ? "分包报价高于参考预算超过此比例时，RFQ 对比表与对比 PDF 显示红色「超预算」徽章。" : "Quotes above the reference budget by more than this % get a red badge in the RFQ compare table and PDF."}</span>
            </div>
          </div>
          <PricingPreview zh={zh} params={params} setParams={setParams} panel={draft.panel} rows={preview} />
        </div>
      )}
      {tab === "panel" && <PanelTable zh={zh} items={draft.panel} canEdit={canEdit} onChange={(v) => patch("panel", v)} />}
      {tab === "reno" && <CatalogTable zh={zh} kind="reno" items={draft.renoItems} builtin={meta.builtinReno} canEdit={canEdit} onChange={(v) => patch("renoItems", v)} hint={zh ? "装修单项 = 数量 × 单价。改价后，之后保存的装修合同明细按新单价重算；内置项不可删除，可将单价设为 0。" : "Renovation items: qty × unit price. Built-in items cannot be deleted."} />}
      {tab === "mat" && <CatalogTable zh={zh} kind="mat" items={draft.matItems} builtin={meta.builtinMat} canEdit={canEdit} onChange={(v) => patch("matItems", v)} hint={zh ? "室内材料目录参考单价：新建材料合同时的默认单价与库存清单依据。" : "Interior material catalog reference prices."} />}
      {tab === "versions" && <PricingVersions api={api} zh={zh} canEdit={canEdit} current={meta.version} onRolledBack={(d) => { apply(d); toast.success(zh ? "已回滚，并保存为新版本" : "Rolled back"); onSaved?.(); }} />}
      {canEdit && tab !== "versions" && (
        <div className="sticky bottom-0 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white/95 backdrop-blur px-3 py-2 shadow-sm" data-testid="trade-pricing-savebar">
          <input data-testid="trade-pricing-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={zh ? "版本备注（如：2026 Q3 木结构人工上调）" : "Version note"} className="flex-1 min-w-[200px] text-xs rounded-lg border border-slate-200 px-2 py-1.5" />
          {dirty && <span className="text-[11px] text-amber-700 font-semibold" data-testid="trade-pricing-dirty">{zh ? "有未保存修改" : "Unsaved changes"}</span>}
          <Button size="sm" data-testid="trade-pricing-save" disabled={saving || !dirty} onClick={save} className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" />{zh ? "保存为新版本" : "Save version"}</>}</Button>
          <Button size="sm" variant="outline" data-testid="trade-pricing-discard" disabled={!dirty || saving} onClick={load} className="h-8 text-xs"><Wrench className="h-3.5 w-3.5 mr-1" />{zh ? "放弃修改" : "Discard"}</Button>
        </div>
      )}
    </div>
  );
};
